<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useAlert } from 'dashboard/composables';
import JoopCopilotAPI from 'dashboard/api/joopCopilot';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  currentChat: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['availability', 'useDraft']);
const { t } = useI18n();
const context = ref(null);
const draft = ref('');
const isLoading = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref('');
let pollTimer = null;
let fetchContext;

const run = computed(() => context.value?.run || null);
const state = computed(() => context.value?.state || 'loading');
const isReady = computed(() => state.value === 'ready' && run.value?.answer);
const isReviewed = computed(() => state.value === 'reviewed');
const isEdited = computed(
  () => isReady.value && draft.value.trim() !== (run.value?.answer || '').trim()
);
const confidenceTone = computed(() => {
  const confidence = run.value?.confidence;
  if (confidence === null || confidence === undefined) return 'slate';
  if (confidence >= 80) return 'teal';
  if (confidence >= 60) return 'amber';
  return 'ruby';
});
const insertEvent = computed(() =>
  [INBOX_TYPES.WEB, INBOX_TYPES.EMAIL].includes(props.currentChat.channel)
    ? BUS_EVENTS.INSERT_INTO_RICH_EDITOR
    : BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR
);

const stopPolling = () => {
  if (pollTimer) window.clearTimeout(pollTimer);
  pollTimer = null;
};

const schedulePoll = () => {
  stopPolling();
  if (state.value === 'generating') {
    pollTimer = window.setTimeout(() => fetchContext(false), 2000);
  }
};

const applyContext = data => {
  context.value = data;
  emit('availability', data?.enabled === true);
  if (data?.run?.answer && !data?.run?.review_status) {
    draft.value = data.run.answer;
  }
  schedulePoll();
};

fetchContext = async (showLoader = true) => {
  if (!props.currentChat.id) return;
  if (showLoader) isLoading.value = true;
  errorMessage.value = '';

  try {
    const { data } = await JoopCopilotAPI.context(props.currentChat.id);
    applyContext(data);
  } catch (error) {
    stopPolling();
    emit('availability', error?.response?.status !== 404);
    errorMessage.value =
      error?.response?.data?.error || t('CONVERSATION.JOOP_COPILOT.ERROR');
  } finally {
    isLoading.value = false;
  }
};

const generateDraft = async (regenerate = false) => {
  isSubmitting.value = true;
  errorMessage.value = '';
  try {
    await JoopCopilotAPI.generate(props.currentChat.id, regenerate);
    context.value = { ...context.value, state: 'generating' };
    schedulePoll();
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error || t('CONVERSATION.JOOP_COPILOT.ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

const useDraft = async () => {
  const content = draft.value.trim();
  if (!content || !run.value) return;

  isSubmitting.value = true;
  errorMessage.value = '';
  try {
    const { data } = await JoopCopilotAPI.review(props.currentChat.id, {
      run_id: run.value.id,
      review_status: isEdited.value ? 'edited' : 'accepted',
      answer: isEdited.value ? content : null,
    });
    applyContext(data);
    emitter.emit(insertEvent.value, content);
    emit('useDraft');
    useAlert(t('CONVERSATION.JOOP_COPILOT.INSERTED'));
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error || t('CONVERSATION.JOOP_COPILOT.ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

const rejectDraft = async () => {
  if (!run.value) return;

  isSubmitting.value = true;
  errorMessage.value = '';
  try {
    const { data } = await JoopCopilotAPI.review(props.currentChat.id, {
      run_id: run.value.id,
      review_status: 'rejected',
    });
    applyContext(data);
    useAlert(t('CONVERSATION.JOOP_COPILOT.REJECTED'));
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error || t('CONVERSATION.JOOP_COPILOT.ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => props.currentChat.id,
  () => {
    stopPolling();
    context.value = null;
    draft.value = '';
    fetchContext();
  },
  { immediate: true }
);

onBeforeUnmount(stopPolling);
</script>

<template>
  <section
    class="flex min-h-0 flex-1 flex-col overflow-y-auto bg-n-surface-1 p-4 text-n-slate-12"
    :aria-label="$t('CONVERSATION.JOOP_COPILOT.TITLE')"
  >
    <div class="flex flex-col gap-4">
      <header class="flex items-start justify-between gap-3">
        <div class="flex min-w-0 items-center gap-3">
          <span
            class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-blue-11"
            aria-hidden="true"
          >
            <Icon icon="i-lucide-sparkles" class="size-5" />
          </span>
          <div class="min-w-0">
            <h2 class="text-base font-semibold leading-6">
              {{ $t('CONVERSATION.JOOP_COPILOT.TITLE') }}
            </h2>
            <p class="text-xs leading-5 text-n-slate-11">
              {{ $t('CONVERSATION.JOOP_COPILOT.SAFE_MODE') }}
            </p>
          </div>
        </div>
        <span
          class="shrink-0 whitespace-nowrap rounded-full bg-n-teal-9/10 px-2 py-1 text-xs font-medium text-n-teal-11"
        >
          {{ $t('CONVERSATION.JOOP_COPILOT.BADGE') }}
        </span>
      </header>

      <div
        v-if="errorMessage"
        role="alert"
        class="flex gap-2 rounded-xl border border-n-ruby-6 bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
      >
        <Icon
          icon="i-lucide-circle-alert"
          class="mt-0.5 size-4 shrink-0"
          aria-hidden="true"
        />
        <span>{{ errorMessage }}</span>
      </div>

      <div
        v-if="isLoading || state === 'loading'"
        class="flex min-h-48 flex-col items-center justify-center gap-3 rounded-2xl border border-n-weak bg-n-surface-2 p-6 text-center"
      >
        <Spinner />
        <p class="text-sm text-n-slate-11">
          {{ $t('CONVERSATION.JOOP_COPILOT.LOADING') }}
        </p>
      </div>

      <div
        v-else-if="state === 'unavailable'"
        class="flex flex-col gap-3 rounded-2xl border border-n-weak bg-n-surface-2 p-4"
      >
        <div class="flex items-center gap-2 font-medium">
          <Icon
            icon="i-lucide-shield-alert"
            class="size-5 text-n-amber-11"
            aria-hidden="true"
          />
          {{ $t('CONVERSATION.JOOP_COPILOT.UNAVAILABLE_TITLE') }}
        </div>
        <p class="text-sm leading-6 text-n-slate-11">
          {{ context?.reason || run?.handoff_reason }}
        </p>
      </div>

      <div
        v-else-if="state === 'idle'"
        class="flex flex-col items-start gap-4 rounded-2xl border border-n-weak bg-n-surface-2 p-4"
      >
        <div class="flex gap-3">
          <Icon
            icon="i-lucide-message-square-text"
            class="mt-0.5 size-5 shrink-0 text-n-blue-11"
            aria-hidden="true"
          />
          <div class="flex flex-col gap-1">
            <p class="font-medium">
              {{ $t('CONVERSATION.JOOP_COPILOT.IDLE_TITLE') }}
            </p>
            <p class="text-sm leading-6 text-n-slate-11">
              {{ $t('CONVERSATION.JOOP_COPILOT.IDLE_DESCRIPTION') }}
            </p>
          </div>
        </div>
        <Button
          :label="$t('CONVERSATION.JOOP_COPILOT.GENERATE')"
          icon="i-lucide-wand-sparkles"
          class="min-h-11"
          :is-loading="isSubmitting"
          @click="generateDraft(false)"
        />
      </div>

      <div
        v-else-if="state === 'generating'"
        aria-live="polite"
        class="flex min-h-48 flex-col items-center justify-center gap-3 rounded-2xl border border-n-weak bg-n-surface-2 p-6 text-center"
      >
        <Spinner />
        <div class="flex flex-col gap-1">
          <p class="font-medium">
            {{ $t('CONVERSATION.JOOP_COPILOT.GENERATING_TITLE') }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ $t('CONVERSATION.JOOP_COPILOT.GENERATING_DESCRIPTION') }}
          </p>
        </div>
      </div>

      <template v-else-if="isReady">
        <div class="grid grid-cols-2 gap-2">
          <div class="rounded-xl border border-n-weak bg-n-surface-2 p-3">
            <p class="text-xs text-n-slate-11">
              {{ $t('CONVERSATION.JOOP_COPILOT.CONFIDENCE') }}
            </p>
            <p
              class="mt-1 text-sm font-semibold"
              :class="{
                'text-n-teal-11': confidenceTone === 'teal',
                'text-n-amber-11': confidenceTone === 'amber',
                'text-n-ruby-11': confidenceTone === 'ruby',
              }"
            >
              {{
                run.confidence === null
                  ? $t('CONVERSATION.JOOP_COPILOT.EMPTY_VALUE')
                  : $t('CONVERSATION.JOOP_COPILOT.CONFIDENCE_VALUE', {
                      value: run.confidence,
                    })
              }}
            </p>
          </div>
          <div class="rounded-xl border border-n-weak bg-n-surface-2 p-3">
            <p class="text-xs text-n-slate-11">
              {{ $t('CONVERSATION.JOOP_COPILOT.STATUS') }}
            </p>
            <p class="mt-1 text-sm font-semibold">
              {{ run.status_label }}
            </p>
          </div>
        </div>

        <div
          v-if="run.requires_handoff"
          class="flex gap-2 rounded-xl border border-n-amber-6 bg-n-amber-3 p-3 text-sm text-n-amber-11"
        >
          <Icon
            icon="i-lucide-user-round-check"
            class="mt-0.5 size-4 shrink-0"
            aria-hidden="true"
          />
          <span>{{ run.handoff_reason }}</span>
        </div>

        <div class="flex flex-col gap-2">
          <label for="joop-copilot-draft" class="text-sm font-medium">
            {{ $t('CONVERSATION.JOOP_COPILOT.DRAFT_LABEL') }}
          </label>
          <textarea
            id="joop-copilot-draft"
            v-model="draft"
            rows="9"
            maxlength="10000"
            class="min-h-52 w-full resize-y rounded-xl border border-n-strong bg-n-background px-3 py-3 text-sm leading-6 text-n-slate-12 outline-none transition-colors focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
          />
          <p class="text-xs leading-5 text-n-slate-11">
            {{ $t('CONVERSATION.JOOP_COPILOT.DRAFT_HELP') }}
          </p>
        </div>

        <div v-if="run.citations?.length" class="flex flex-col gap-2">
          <h3 class="text-sm font-medium">
            {{ $t('CONVERSATION.JOOP_COPILOT.SOURCES') }}
          </h3>
          <ol class="flex flex-col gap-2">
            <li
              v-for="(citation, index) in run.citations"
              :key="`${citation.source_name}-${index}`"
              class="rounded-xl border border-n-weak bg-n-surface-2 p-3"
            >
              <p class="text-sm font-medium">
                {{ citation.source_name }}
                <span
                  v-if="citation.position"
                  class="font-normal text-n-slate-11"
                >
                  {{
                    $t('CONVERSATION.JOOP_COPILOT.FRAGMENT', {
                      number: citation.position,
                    })
                  }}
                </span>
              </p>
              <p
                v-if="citation.excerpt"
                class="mt-1 text-xs leading-5 text-n-slate-11"
              >
                {{ citation.excerpt }}
              </p>
            </li>
          </ol>
        </div>

        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Button
            :label="$t('CONVERSATION.JOOP_COPILOT.USE_DRAFT')"
            icon="i-lucide-corner-down-left"
            class="min-h-11"
            :is-loading="isSubmitting"
            @click="useDraft"
          />
          <Button
            :label="$t('CONVERSATION.JOOP_COPILOT.REJECT')"
            icon="i-lucide-x"
            color="slate"
            variant="outline"
            class="min-h-11"
            :disabled="isSubmitting"
            @click="rejectDraft"
          />
        </div>
      </template>

      <div
        v-else-if="isReviewed"
        class="flex flex-col gap-4 rounded-2xl border border-n-weak bg-n-surface-2 p-4"
      >
        <div class="flex gap-3">
          <span
            class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-teal-9/10 text-n-teal-11"
            aria-hidden="true"
          >
            <Icon icon="i-lucide-check" class="size-5" />
          </span>
          <div class="flex flex-col gap-1">
            <p class="font-medium">{{ run.review_status_label }}</p>
            <p class="text-sm leading-6 text-n-slate-11">
              {{ $t('CONVERSATION.JOOP_COPILOT.REVIEWED_DESCRIPTION') }}
            </p>
          </div>
        </div>
        <Button
          :label="$t('CONVERSATION.JOOP_COPILOT.REGENERATE')"
          icon="i-lucide-refresh-cw"
          color="slate"
          variant="outline"
          class="min-h-11 self-start"
          :is-loading="isSubmitting"
          @click="generateDraft(true)"
        />
      </div>

      <div
        v-else-if="state === 'error'"
        class="flex flex-col items-start gap-4 rounded-2xl border border-n-ruby-6 bg-n-ruby-3 p-4"
      >
        <div class="flex gap-3">
          <Icon
            icon="i-lucide-triangle-alert"
            class="mt-0.5 size-5 shrink-0 text-n-ruby-11"
            aria-hidden="true"
          />
          <div class="flex flex-col gap-1">
            <p class="font-medium text-n-ruby-11">
              {{ $t('CONVERSATION.JOOP_COPILOT.ERROR_TITLE') }}
            </p>
            <p class="text-sm leading-6 text-n-ruby-11">
              {{ run?.handoff_reason || $t('CONVERSATION.JOOP_COPILOT.ERROR') }}
            </p>
          </div>
        </div>
        <Button
          :label="$t('CONVERSATION.JOOP_COPILOT.RETRY')"
          icon="i-lucide-refresh-cw"
          color="slate"
          variant="outline"
          class="min-h-11"
          :is-loading="isSubmitting"
          @click="generateDraft(true)"
        />
      </div>
    </div>
  </section>
</template>
